if (!customElements.get('shop-tv-shelf')) {
  customElements.define('shop-tv-shelf', class ShopTvShelf extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.returnTargets = new WeakMap();
      const { signal } = this.controller;
      this.rail = this.querySelector('[data-tv-rail]');
      this.controls = this.querySelector('[data-tv-controls]');
      this.activeFilter = 'all';
      this.addEventListener('click', (event) => this.onClick(event), { signal });
      document.addEventListener('click', (event) => this.onDocumentClick(event), { signal });
      document.addEventListener('shopify:block:select', (event) => this.onBlockSelect(event), { signal });
      this.querySelectorAll('dialog[data-tv-film]').forEach((dialog) => {
        dialog.addEventListener('close', () => this.onClose(dialog), { signal });
      });
      window.addEventListener('hashchange', () => this.openFromHash(), { signal });
      window.addEventListener('resize', () => this.updateControls(), { passive: true, signal });
      this.rail?.addEventListener('scroll', () => this.updateControls(), { passive: true, signal });
      if (this.rail && 'ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.updateControls());
        this.resizeObserver.observe(this.rail);
      }
      this.initFrame = requestAnimationFrame(() => {
        if (this.id === 'kurzfilme') this.applyFilter('all');
        this.updateControls();
        this.openFromHash();
      });
    }

    disconnectedCallback() {
      this.querySelectorAll('dialog[data-tv-film][open]').forEach((dialog) => dialog.close());
      this.controller?.abort();
      this.controller = null;
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.initFrame);
    }

    onClick(event) {
      if (!(event.target instanceof Element)) return;
      const opener = event.target.closest('[data-tv-open]');
      if (opener && this.contains(opener)) {
        const dialog = document.getElementById(opener.dataset.tvOpen);
        if (dialog instanceof HTMLDialogElement && this.contains(dialog)) this.open(dialog, opener);
        return;
      }
      const closeButton = event.target.closest('[data-tv-close]');
      if (closeButton && this.contains(closeButton)) {
        closeButton.closest('dialog')?.close();
        return;
      }
      const scrollButton = event.target.closest('[data-tv-scroll]');
      if (scrollButton && this.contains(scrollButton) && this.rail) {
        const direction = scrollButton.dataset.tvScroll === 'previous' ? -1 : 1;
        this.rail.scrollBy({
          left: direction * Math.max(240, this.rail.clientWidth * 0.85),
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
        });
        return;
      }
      if (event.target instanceof HTMLDialogElement && event.target.hasAttribute('data-tv-film')) {
        const bounds = event.target.getBoundingClientRect();
        const outside = event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom;
        if (outside) event.target.close();
      }
    }

    open(dialog, opener) {
      if (dialog.open || typeof dialog.showModal !== 'function') return;
      if (dialog.closest('.stv-rail__item')?.hidden) this.applyFilter('all');
      document.querySelectorAll('dialog.stv-dialog[open]').forEach((other) => other.close());
      const returnTarget = opener || document.activeElement;
      if (returnTarget instanceof HTMLElement) this.returnTargets.set(dialog, returnTarget);
      dialog.showModal();
    }

    onClose(dialog) {
      const target = this.returnTargets.get(dialog);
      if (target?.isConnected && !target.closest('[hidden]') && !document.querySelector('dialog.stv-dialog[open]')) target.focus({ preventScroll: true });
      this.returnTargets.delete(dialog);
    }

    onDocumentClick(event) {
      if (!(event.target instanceof Element)) return;
      const slugButton = event.target.closest('[data-tv-open-slug]');
      if (slugButton) {
        const dialog = Array.from(this.querySelectorAll('dialog[data-tv-film]')).find((item) => item.dataset.tvSlug === slugButton.dataset.tvOpenSlug);
        if (dialog instanceof HTMLDialogElement) {
          event.preventDefault();
          this.open(dialog, slugButton);
        }
      }
      if (this.id !== 'kurzfilme') return;
      const filterButton = event.target.closest('[data-tv-filter]');
      if (!filterButton) return;
      const filter = filterButton.dataset.tvFilter;
      const available = Array.from(this.querySelectorAll('[data-tv-genres]')).some((item) => this.genres(item).includes(filter));
      if (filter !== 'all' && !available) return;
      event.preventDefault();
      this.applyFilter(filter, filterButton);
      this.scrollIntoView({ block: 'start', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    }

    genres(item) {
      return (item.dataset.tvGenres || '').split(',').map((value) => value.trim()).filter(Boolean);
    }

    applyFilter(filter, trigger) {
      if (this.id !== 'kurzfilme') return;
      this.activeFilter = filter;
      this.querySelectorAll('.stv-rail__item[data-tv-genres]').forEach((item) => {
        item.hidden = filter !== 'all' && !this.genres(item).includes(filter);
        if (item.hidden) {
          item.querySelectorAll('dialog[data-tv-film][open]').forEach((dialog) => {
            if (trigger instanceof HTMLElement) this.returnTargets.set(dialog, trigger);
            else this.returnTargets.delete(dialog);
            dialog.close();
          });
        }
      });
      document.querySelectorAll('[data-tv-filter]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.tvFilter === filter)));
      this.rail?.scrollTo({ left: 0, behavior: 'instant' });
      this.updateControls();
    }

    onBlockSelect(event) {
      if (!window.Shopify?.designMode) return;
      const blockId = event.detail?.blockId;
      const card = Array.from(this.querySelectorAll('[data-tv-block-id]')).find((item) => item.dataset.tvBlockId === blockId);
      if (!card) return;
      if (card.closest('.stv-rail__item')?.hidden) this.applyFilter('all');
      card.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' });
    }

    openFromHash() {
      let slug;
      try {
        slug = decodeURIComponent(window.location.hash.slice(1));
      } catch {
        return;
      }
      if (!slug.startsWith('film-')) return;
      const dialog = Array.from(this.querySelectorAll('dialog[data-tv-film]')).find((item) => item.dataset.tvSlug === slug);
      if (!(dialog instanceof HTMLDialogElement)) return;
      const opener = Array.from(this.querySelectorAll('[data-tv-open]')).find((item) => item.dataset.tvOpen === dialog.id);
      this.open(dialog, opener);
    }

    updateControls() {
      if (!this.rail || !this.controls) return;
      const overflow = this.rail.scrollWidth - this.rail.clientWidth;
      this.controls.hidden = overflow <= 2;
      const previous = this.controls.querySelector('[data-tv-scroll="previous"]');
      const next = this.controls.querySelector('[data-tv-scroll="next"]');
      if (previous) previous.disabled = this.rail.scrollLeft <= 2;
      if (next) next.disabled = this.rail.scrollLeft >= overflow - 2;
    }
  });
}
